/* Card 5 — location page behaviour. Progressive enhancement only: with JavaScript off the page
 * still shows every store, every postcode and every card, it just does not highlight or auto-collapse.
 *
 *  1. rail dots     — one dot per card, tracking which card is in view
 *  2. store <-> pin  — tapping a pin opens and highlights its store row, and vice versa
 *  3. phone rows     — below 75rem all but the selected store collapse (they render open for crawlers)
 *  4. review drift   — the reviews rail creeps while the cursor is over it, off under reduced motion
 */
(function () {
  'use strict';

  /* Several sections reference this file, so the browser may execute it more than once. Without
     this guard the rails would be wired twice and every store row would carry duplicate handlers. */
  if (window.__c5LocationInit) return;
  window.__c5LocationInit = true;

  var DESKTOP = window.matchMedia('(min-width: 75rem)');
  /* The layout breakpoint for the rails is 60rem, not the 75rem the store rows use. This was
     missing when wireScrollRail first shipped: the resulting ReferenceError threw on init's very
     first rail and took the whole init with it, so the tickers, the default tier AND the store
     highlighting all died together. One undeclared variable, three broken features. */
  var WIDE = window.matchMedia('(min-width: 60rem)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- 1. rail dots ---------- */
  function wireDots(rail) {
    var host = rail.parentElement && rail.parentElement.querySelector('[data-c5-dots]');
    if (!host) return;
    var cards = Array.prototype.slice.call(rail.children);
    if (cards.length < 2) return;

    host.innerHTML = '';
    cards.forEach(function (_, i) {
      var dot = document.createElement('span');
      if (i === 0) dot.className = 'is-active';
      host.appendChild(dot);
    });

    var dots = Array.prototype.slice.call(host.children);
    var mark = function (i) {
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
    };

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) mark(cards.indexOf(e.target));
          });
        },
        { root: rail, threshold: 0.6 }
      );
      cards.forEach(function (c) { io.observe(c); });
    } else {
      rail.addEventListener('scroll', function () {
        var i = Math.round(rail.scrollLeft / (rail.scrollWidth / cards.length));
        mark(Math.min(Math.max(i, 0), cards.length - 1));
      }, { passive: true });
    }
  }

  /* ---------- 2 + 3. stores ---------- */
  function wireStores() {
    var list = document.querySelector('[data-c5-store-list]');
    if (!list) return;
    var rows = Array.prototype.slice.call(list.querySelectorAll('[data-c5-store]'));
    var pins = Array.prototype.slice.call(document.querySelectorAll('[data-c5-pin]'));
    if (!rows.length) return;

    var selected = list.querySelector('.c5-store--selected');
    var current = selected ? selected.getAttribute('data-c5-store') : rows[0].getAttribute('data-c5-store');

    function select(key, scroll) {
      current = key;
      rows.forEach(function (row) {
        var isIt = row.getAttribute('data-c5-store') === key;
        row.classList.toggle('c5-store--selected', isIt);
        if (!DESKTOP.matches) row.open = isIt;
        if (isIt && scroll && typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ block: 'nearest', behavior: REDUCED.matches ? 'auto' : 'smooth' });
        }
      });
      pins.forEach(function (pin) {
        pin.setAttribute('aria-current', pin.getAttribute('data-c5-pin') === key ? 'true' : 'false');
      });
    }

    pins.forEach(function (pin) {
      pin.addEventListener('click', function () { select(pin.getAttribute('data-c5-pin'), true); });
    });

    rows.forEach(function (row) {
      row.addEventListener('toggle', function () {
        if (row.open) select(row.getAttribute('data-c5-store'), false);
      });

      /* On desktop every row is opened above and the +/- affordance is hidden,
         but the <details> element still toggled natively — so clicking a tile
         collapsed it and hid the address, hours and phone (Jeremy 2026-09-21).
         The toggle listener only re-selects when the row is OPEN, so nothing
         ever reopened it. Suppress the default on the SUMMARY rather than the
         row: the click still runs, so tapping a tile keeps highlighting its
         pin, and the phone behaviour is untouched. */
      var summary = row.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (event) {
        if (!DESKTOP.matches) return;
        event.preventDefault();
        select(row.getAttribute('data-c5-store'), false);
      });
    });

    function applyWidth() {
      if (DESKTOP.matches) {
        rows.forEach(function (row) { row.open = true; });
      } else {
        rows.forEach(function (row) { row.open = row.getAttribute('data-c5-store') === current; });
      }
    }
    applyWidth();
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', applyWidth);
    select(current, false);
  }

  /* ---------- 4. review drift ---------- */
  function wireDrift(rail) {
    if (REDUCED.matches) return;
    var timer = null;
    var step = function () {
      if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1) {
        rail.scrollLeft = 0;
      } else {
        rail.scrollLeft += 1;
      }
    };
    var start = function () { if (!timer) timer = window.setInterval(step, 40); };
    var stop = function () { window.clearInterval(timer); timer = null; };
    rail.addEventListener('mouseenter', start);
    rail.addEventListener('mouseleave', stop);
    rail.addEventListener('focusin', stop);
    rail.addEventListener('touchstart', stop, { passive: true });
  }

  /* ---------- 5. default card ----------
   * The tiers rail opens on the middle tile (the 6ft Homemaker), not the smallest one: it is the
   * size most people buy, and it is the one drawn raised. Detected from the markup so nothing
   * has to be configured twice. */
  function centreDefault(rail) {
    var star = rail.querySelector('.c5-size--raised');
    if (!star || rail.scrollWidth <= rail.clientWidth) return;
    var target = star.offsetLeft - (rail.clientWidth - star.offsetWidth) / 2;
    rail.scrollLeft = Math.max(target, 0);
  }

  /* ---------- 6. scroll-driven ticker ----------
   * Jeremy, 2026-09-18: on a phone the stat band and the how-it-works steps should roll through
   * their items as the customer scrolls past, rather than waiting to be swiped. The rail's
   * scrollLeft is mapped to how far the section has travelled through the viewport, so it is
   * driven entirely by the scroll position and needs no timer: the moment the reader stops, it
   * stops. Left alone under prefers-reduced-motion and above the desktop breakpoint, where all
   * the items are already visible side by side. */
  function wireScrollRail(rail) {
    if (!rail || REDUCED.matches) return;
    var frame = null;

    function update() {
      frame = null;
      if (WIDE.matches) return;
      var max = rail.scrollWidth - rail.clientWidth;
      if (max <= 0) return;
      var box = rail.getBoundingClientRect();
      /* 0 when the rail's top reaches the bottom of the screen, 1 once it has fully passed */
      var span = window.innerHeight + box.height;
      var progress = span > 0 ? (window.innerHeight - box.top) / span : 0;
      progress = Math.min(Math.max(progress, 0), 1);
      rail.scrollLeft = progress * max;
    }

    function onScroll() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-c5-rail]')).forEach(function (rail) {
      if (rail.hasAttribute('data-c5-scroll-rail')) {
        wireScrollRail(rail);
      } else {
        centreDefault(rail);
        wireDots(rail);
      }
      if (rail.hasAttribute('data-c5-drift')) wireDrift(rail);
    });

    /* The how-it-works steps are Dawn's own multicolumn section, whose mobile slider is a
       <ul class="slider">. Driving that element gives it the same behaviour without forking
       a shared Dawn section, which Card 4 owns. */
    wireScrollRail(document.querySelector('[id*="card5_steps"] .slider'));

    wireStores();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
